/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import {
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';

import {
    BehaviorSubject,
    combineLatest,
    Subject,
} from 'rxjs';

import {
    distinctUntilChanged,
    map,
    takeUntil,
} from 'rxjs/operators';

import { e2eTimingGroupByKeys } from 'ng/modules/logs/constants/logs-signpost.constants';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { TrafficLogsSignpostStore } from '../../../traffic-logs/traffic-logs-sidebar/traffic-logs-signpost.store';
import './logs-signpost-custom-e2e-timing.component.less';
import { getEndToEndTimingRequestParams } from '../../../../utils/logs-signpost.utils';
import { ITrafficLogsStates, TrafficLogsStore } from '../../../traffic-logs/traffic-logs.store';

/**
 * @description Signpost component for traffic log signpost custom end to end timing data.
 * @author Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'logs-signpost-custom-e2e-timing',
    templateUrl: './logs-signpost-custom-e2e-timing.component.html',
})
export class LogsSignpostCustomEndToEndTimingComponent implements OnInit, OnDestroy {
    /**
     * The ID of the pool used to filter logs from a VS.
     */
    @Input()
    public poolId?: string;

    private readonly destroy$ = new Subject<void>();
    private readonly selectedKey$ = new BehaviorSubject<string>(e2eTimingGroupByKeys.totalTime);

    constructor(
        public readonly trafficLogsSignpostStore: TrafficLogsSignpostStore,
        private readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
    ) {}

    /** @override */
    public ngOnInit(): void {
        combineLatest([
            this.selectedKey$.pipe(distinctUntilChanged()),
            this.trafficLogsStore.vsConfig$.pipe(distinctUntilChanged()),
        ]).pipe(
            takeUntil(this.destroy$),
            map(([key, vsConfig]) => getEndToEndTimingRequestParams(key, vsConfig)),
        ).subscribe(fullSignpostParams => {
            fullSignpostParams.forEach(param => {
                addPoolIdFilterParams(param.requestParams, this.poolId);
            });

            this.trafficLogsSignpostStore.getLogSignpostCombinedData(fullSignpostParams);
        });
    }

    /**
     * Switch view for end to end timing signpost and get data for new field.
     */
    public switchView(key: string): void {
        this.selectedKey$.next(key);
    }

    public trackbyIndex(index: number): number {
        return index;
    }

    /** @override */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
